import {
  isAaveV4Type,
  isCompoundV3Type,
  isCurvance,
  isFraxlend,
  isMorphoBlue,
  isExactly,
  isFluid,
  isGearboxV3,
  isInverse,
  isLiquityFamily,
  isMidnight,
  isRiver,
  isTeller,
  isTerm,
  isTermMax,
  isTwyne,
  isUsdd,
} from '@1delta/lender-registry'
import type { TermSheetInput } from '../input'
import type { DeepPartial, TermSheet } from '../types'

import { exactlyAdapter } from './exactly'
import { midnightAdapter } from './midnight'
import { termAdapter } from './term'
import { termMaxAdapter } from './termmax'
import { tellerAdapter } from './teller'
import { listaBrokerAdapter } from './lista'
import { liquityAdapter } from './liquity'
import { riverAdapter } from './river'
import { inverseAdapter } from './inverse'
import { coolerAdapter } from './cooler'
import { dssAdapter } from './dss'
import { compoundV3Adapter } from './compoundV3'
import { fluidAdapter } from './fluid'
import { gearboxAdapter } from './gearbox'
import { llamaLendAdapter } from './llamalend'
import { frankencoinAdapter } from './frankencoin'
import { resupplyAdapter } from './resupply'
import { curvanceAdapter } from './curvance'
import { flyingTulipAdapter } from './flyingTulip'
import { twyneAdapter } from './twyne'
import { fraxlendAdapter } from './fraxlend'
import { morphoBlueAdapter } from './morphoBlue'
import { aaveV4Adapter } from './aaveV4'

/**
 * A term-sheet adapter: returns ONLY what the generic builder cannot derive,
 * as a `DeepPartial<TermSheet>` merged over the generic result.
 *
 * Adding a lender is one adapter plus one profile entry — no core edits. The
 * completeness test fails when a lender family reaches production without a
 * profile, so the extension point cannot be silently skipped.
 */
export interface TermAdapter {
  /** Stable id, for tests and debugging. */
  id: string
  /** Does this adapter handle the given lender key? */
  matches: (lender: string) => boolean
  /** The profile whose prose this market points at. */
  profileId: (input: TermSheetInput) => string
  build: (input: TermSheetInput) => DeepPartial<TermSheet>
}

/**
 * Order matters only where predicates could overlap; today they are disjoint.
 * The list is walked front-to-back and the first match wins.
 */
export const TERM_ADAPTERS: TermAdapter[] = [
  exactlyAdapter,
  midnightAdapter,
  termAdapter,
  termMaxAdapter,
  tellerAdapter,
  listaBrokerAdapter,
  liquityAdapter,
  riverAdapter,
  inverseAdapter,
  coolerAdapter,
  dssAdapter,
  compoundV3Adapter,
  fluidAdapter,
  gearboxAdapter,
  llamaLendAdapter,
  frankencoinAdapter,
  resupplyAdapter,
  curvanceAdapter,
  flyingTulipAdapter,
  twyneAdapter,
  fraxlendAdapter,
  morphoBlueAdapter,
  aaveV4Adapter,
]

export function resolveAdapter(lender: string): TermAdapter | undefined {
  return TERM_ADAPTERS.find((a) => a.matches(lender))
}

// Re-exported so the predicates used by adapters stay in one import site.
export {
  isAaveV4Type,
  isCompoundV3Type,
  isExactly,
  isFluid,
  isGearboxV3,
  isInverse,
  isLiquityFamily,
  isMidnight,
  isRiver,
  isTeller,
  isTerm,
  isTermMax,
  isUsdd,
}
